import {
  SessionRole,
  useGetSession,
  useGetSessionAttendees,
} from '@sunrise-ui/api-client';
import {
  ProfileBio,
  ProfileResult,
  SessionCardTags,
  SessionEditButton,
  withAuthGate,
} from '@sunrise-ui/composites';
import type { WithResponsive } from '@sunrise-ui/primitives';
import {
  Colors,
  ExpandableText,
  Loader,
  PageContainer,
  TextBold,
  TextReg,
  useAuthStore,
  useDynamicStyles,
  useSafeLocalSearchParams,
} from '@sunrise-ui/primitives';
import { router } from 'expo-router';
import React from 'react';
import { ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';

function SessionReviewPage() {
  const { sessionId } = useSafeLocalSearchParams<{
    sessionId: string;
  }>();
  const styles = useDynamicStyles(createStyles);
  const { isAuthenticated, currentUserId } = useAuthStore();

  const { data: session, isLoading: isLoadingSession } =
    useGetSession(sessionId);
  const { data: speakers } = useGetSessionAttendees(sessionId, true);

  // Check if user is authorized to edit this session (speaker or moderator)
  const userCanEditSession = React.useMemo(() => {
    if (!isAuthenticated || !currentUserId || !session || !speakers) {
      return false;
    }

    // Check if user is the session creator
    if (session.user_id === currentUserId) return true;

    // Check if user is a speaker or moderator
    const userSpeaker = speakers.find(
      (speaker) => speaker.user_id === currentUserId,
    );
    return (
      userSpeaker?.role === SessionRole.SPEAKER ||
      userSpeaker?.role === SessionRole.MODERATOR
    );
  }, [isAuthenticated, currentUserId, session, speakers]);

  // Show loading while session data is loading
  if (isLoadingSession) {
    return (
      <PageContainer>
        <View style={styles.container}>
          <Loader style={styles.loader} />
        </View>
      </PageContainer>
    );
  }

  // Show access denied message if user cannot edit the session
  if (session && !userCanEditSession) {
    return (
      <PageContainer>
        <View style={styles.container}>
          <View style={styles.accessDeniedContainer}>
            <TextReg style={styles.accessDeniedText}>
              You don&apos;t have access to edit this session
            </TextReg>
          </View>
        </View>
      </PageContainer>
    );
  }

  const eventId = session?.event_id;

  return (
    <PageContainer>
      <View style={styles.container}>
        <ScrollView>
          <View style={styles.sessionInfo}>
            <View style={styles.titleRow}>
              <TextBold style={styles.title}>{session?.topic_name}</TextBold>
              <SessionEditButton
                canEdit={userCanEditSession}
                sessionId={sessionId}
                eventId={eventId ?? ''}
              />
            </View>
            <View style={styles.tagRow}>
              <SessionCardTags sessionId={sessionId} eventId={eventId ?? ''} />
            </View>
            <ExpandableText text={session?.topic_description} charLimit={350} />
          </View>
          {speakers?.map((speaker) => {
            const handleProfilePress = () => {
              if (speaker.user_id) {
                router.navigate(`/to/${speaker.user_id}`);
              }
            };

            return (
              <TouchableOpacity
                key={speaker.user_id}
                style={styles.speakerInfo}
                onPress={handleProfilePress}
              >
                <ProfileResult userId={speaker.user_id} size="small" />
                <ProfileBio userId={speaker.user_id} />
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      </View>
    </PageContainer>
  );
}

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: Colors.purple0,
    },
    loader: {
      marginTop: 50,
    },
    sessionInfo: {
      backgroundColor: Colors.purple0,
      borderRadius: 12,
      padding: 20,
    },
    titleRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 16,
    },
    title: {
      fontSize: isMobile ? 20 : 24,
      flex: 1,
      marginRight: 16,
    },
    tagRow: {
      marginBottom: 16,
    },
    speakerInfo: {
      backgroundColor: Colors.purple0,
      borderRadius: 12,
      padding: 20,
      marginBottom: 12,
      shadowColor: Colors.blackOpacity80,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 2,
    },
    accessDeniedContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 20,
    },
    accessDeniedText: {
      fontSize: 16,
      textAlign: 'center',
    },
  });

export default withAuthGate(SessionReviewPage);
